// Autosave for one trip's category, purpose and notes: a category saves when picked, text a moment
// after typing stops or when the field loses focus. Mounted per trip (keyed by id), so leaving a
// trip saves what was typed.

import { useCallback, useEffect, useRef, useState } from "react";
import { useAccount } from "../../data/AccountData.tsx";
import type { Trip } from "../../domain/model.ts";

export type SaveStatus = { kind: "idle" } | { kind: "saving" } | { kind: "saved" } | { kind: "failed"; message: string };

interface Draft {
  categoryRaw: string | null;
  purpose: string;
  notes: string;
}

const textDelay = 1200;

function cleaned(text: string): string | null {
  const value = text.trim();
  return value ? value : null;
}

export function useTripEditor(trip: Trip, options: { onSubscriptionEnded?: () => void; onFailed?: (retry: () => void) => void } = {}) {
  const { editTrip } = useAccount();
  const [draft, setDraft] = useState<Draft>({ categoryRaw: trip.categoryRaw, purpose: trip.purpose ?? "", notes: trip.notes ?? "" });
  const [status, setStatus] = useState<SaveStatus>({ kind: "idle" });
  const latest = useRef(draft);
  const saved = useRef<Draft>(draft);
  const timer = useRef<number | undefined>(undefined);
  const callbacks = useRef(options);
  callbacks.current = options;

  const save = useCallback(async (next: Draft) => {
    window.clearTimeout(timer.current);
    const before = saved.current;
    if (before.categoryRaw === next.categoryRaw && cleaned(before.purpose) === cleaned(next.purpose) && cleaned(before.notes) === cleaned(next.notes)) {
      return;
    }
    setStatus({ kind: "saving" });
    // Counted as saved while in flight, so leaving the trip straight after a pick doesn't send it twice.
    saved.current = next;
    const result = await editTrip(trip.id, { categoryRaw: next.categoryRaw, purpose: cleaned(next.purpose), notes: cleaned(next.notes) });
    if (result.ok) {
      setStatus({ kind: "saved" });
    } else {
      if (saved.current === next) saved.current = before;
      setStatus({ kind: "failed", message: "Couldn’t save this change. Your edit is still here." });
      if (result.subscriptionEnded) callbacks.current.onSubscriptionEnded?.();
      else callbacks.current.onFailed?.(() => void save(latest.current));
    }
  }, [editTrip, trip.id]);

  const update = useCallback((change: Partial<Draft>, when: "now" | "soon") => {
    const next = { ...latest.current, ...change };
    latest.current = next;
    setDraft(next);
    window.clearTimeout(timer.current);
    if (when === "now") void save(next);
    else timer.current = window.setTimeout(() => void save(latest.current), textDelay);
  }, [save]);

  // Leaving the trip saves anything still waiting.
  useEffect(() => () => {
    window.clearTimeout(timer.current);
    void save(latest.current);
  }, [save]);

  return {
    draft,
    status,
    setCategory: (categoryRaw: string | null) => update({ categoryRaw }, "now"),
    setPurpose: (purpose: string) => update({ purpose }, "soon"),
    setNotes: (notes: string) => update({ notes }, "soon"),
    flush: () => void save(latest.current),
    retry: () => void save(latest.current),
  };
}
